import { useEffect } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSwUpdate } from "@/hooks/use-sw-update";

/** One stable id, so a re-raise replaces the prompt instead of stacking one. */
const TOAST_ID = "sw-update";

/**
 * The prompt half of the service worker's update flow: a new worker installs
 * and waits, and the tab reloads only when the user says so — an automatic
 * reload would discard a half-filled test.
 *
 * It renders nothing and calls sonner directly rather than through `useToast`,
 * whose three actions all auto-dismiss and carry no action affordance. Copy
 * comes from the `company` namespace, so `t` names it: the default namespace is
 * `common`, and an unnamed namespace would render the raw key.
 */
export function SwUpdateToast(): null {
  const { needRefresh, applyUpdate, dismiss } = useSwUpdate();
  const { t } = useTranslation("company");

  useEffect(() => {
    if (!needRefresh) {
      toast.dismiss(TOAST_ID);
      return;
    }
    toast(t("pwa.update.message"), {
      id: TOAST_ID,
      duration: Infinity,
      action: { label: t("pwa.update.reload"), onClick: applyUpdate },
      cancel: { label: t("pwa.update.later"), onClick: dismiss },
    });
  }, [needRefresh, applyUpdate, dismiss, t]);

  return null;
}
